import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import logger from '../utils/logger';
import { API_ENDPOINTS } from '../config/api';

const  DriverRatingModal = ({ isOpen, onClose, requestId, driverId, driverName, customerPhone }) => {
  const [ratings, setRatings] = useState({
    reliability: 0,
    punctuality: 0,
    itemIntegrity: 0,
    courtesy: 0
  });
  const [booleanRatings, setBooleanRatings] = useState({
    priceAccuracy: false,
    wouldRecommend: false,
    wouldUseAgain: false
  });
  const [additionalComments, setAdditionalComments] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleStarClick = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleBooleanChange = (category, value) => {
    setBooleanRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that all star ratings are selected
    const missingStarRatings = Object.entries(ratings).filter(([key, value]) => value === 0);
    if (missingStarRatings.length > 0) {
      toast.error('אנא דרג את כל הקטגוריות');
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API_ENDPOINTS.RATINGS}/submit`, {
        requestId,
        driverId,
        customerPhone,
        ...ratings,
        ...booleanRatings,
        additionalComments
      });

      toast.success('הדירוג נשלח בהצלחה!');
      onClose();
      
      // Reset form
      setRatings({
        reliability: 0,
        punctuality: 0,
        itemIntegrity: 0,
        courtesy: 0
      });
      setBooleanRatings({
        priceAccuracy: false,
        wouldRecommend: false,
        wouldUseAgain: false
      });
      setAdditionalComments('');
      
    } catch (error) {
      logger.error('Error submitting rating:', error);
      toast.error(error.response?.data?.message || 'שגיאה בשליחת הדירוג');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-brown-900">דרג את המוביל</h2>
          <button
            onClick={onClose}
            className="text-brown-400 hover:text-brown-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6 p-4 bg-orange-50 rounded-lg">
          <p className="text-brown-700">
            <strong>מוביל:</strong> {driverName}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Star Rating Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-brown-900 mb-4">דרג את המוביל (1-5 כוכבים)</h3>
            
            {[
              { key: 'reliability', label: 'אמינות' },
              { key: 'punctuality', label: 'הגעה בזמן' },
              { key: 'itemIntegrity', label: 'שמירה על שלמות החפצים' },
              { key: 'courtesy', label: 'אדיבות' }
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <label className="text-brown-700 font-medium">{label}</label>
                <div className="flex space-x-1 space-x-reverse">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => handleStarClick(key, value)}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`h-6 w-6 ${
                          value <= ratings[key]
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Boolean Rating Categories */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-brown-900 mb-4">שאלות נוספות</h3>
            
            <div className="space-y-3">
              <fieldset className="flex items-center justify-between">
                <legend className="text-brown-700 font-medium">האם המחיר שהוצע היה המחיר שנגבה בפועל?</legend>
                <div className="flex space-x-4 space-x-reverse">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="priceAccuracy"
                      checked={booleanRatings.priceAccuracy === true}
                      onChange={() => handleBooleanChange('priceAccuracy', true)}
                      className="mr-2"
                    />
                    כן
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="priceAccuracy"
                      checked={booleanRatings.priceAccuracy === false}
                      onChange={() => handleBooleanChange('priceAccuracy', false)}
                      className="mr-2"
                    />
                    לא
                  </label>
                </div>
              </fieldset>

              <fieldset className="flex items-center justify-between">
                <legend className="text-brown-700 font-medium">האם היית ממליץ לחבר?</legend>
                <div className="flex space-x-4 space-x-reverse">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wouldRecommend"
                      checked={booleanRatings.wouldRecommend === true}
                      onChange={() => handleBooleanChange('wouldRecommend', true)}
                      className="mr-2"
                    />
                    כן
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wouldRecommend"
                      checked={booleanRatings.wouldRecommend === false}
                      onChange={() => handleBooleanChange('wouldRecommend', false)}
                      className="mr-2"
                    />
                    לא
                  </label>
                </div>
              </fieldset>

              <fieldset className="flex items-center justify-between">
                <legend className="text-brown-700 font-medium">האם היית סוגר איתו שוב הובלה?</legend>
                <div className="flex space-x-4 space-x-reverse">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wouldUseAgain"
                      checked={booleanRatings.wouldUseAgain === true}
                      onChange={() => handleBooleanChange('wouldUseAgain', true)}
                      className="mr-2"
                    />
                    כן
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="wouldUseAgain"
                      checked={booleanRatings.wouldUseAgain === false}
                      onChange={() => handleBooleanChange('wouldUseAgain', false)}
                      className="mr-2"
                    />
                    לא
                  </label>
                </div>
              </fieldset>
            </div>
          </div>

          {/* Additional Comments */}
          <div>
            <label htmlFor="additional-comments" className="form-label">הערות נוספות (אופציונלי)</label>
            <textarea
              id="additional-comments"
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              maxLength={1000}
              className="input-field"
              placeholder="שתף את חווייתך..."
              rows={3}
            />
            <p className="text-sm text-brown-500 mt-1">
              {additionalComments.length}/1000 תווים
            </p>
          </div>

          {/* Submit Buttons */}
          <div className="flex space-x-3 space-x-reverse pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
              disabled={isSubmitting}
            >
              ביטול
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'שולח...' : 'שלח דירוג'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverRatingModal;
